// The user app's frame, ChatGPT style (plan C1). The admin console keeps
// shell/Shell.tsx. A grid sized to the viewport: the document never scrolls,
// main (or the chat's own column) is the scroller.
//
//   below 768    pinned header: Buka menu (the sidebar as an overlay drawer),
//                the mark and the screen's name, Cari Paket, Chat baru.
//                Below 640 the JAL app-shell bottom bar too: Chat, the first
//                three of Project (Penyedia), Generate Dokumen, Regulasi,
//                Dokumen, E-Learning, then Profil (guest: Chat, Bantuan, Masuk).
//   768 to 1023  a 64px rail (Buka menu, Chat baru, the menu icons,
//                the profile); Buka menu opens the full sidebar as an
//                overlay drawer.
//   from 1024    the full 288px sidebar, collapsible to the rail (remembered
//                per browser).
//
// Cari Paket (shell/SearchProjects.tsx) is an icon button at the top right:
// in the phone header, and from 768 in a slim top bar above main. Only a
// signed-in user with the projects feature has it.
//
// The sidebar, top to bottom: brand and the collapse button; Chat baru and
// the menu (one order for every role, no group titles; shell/nav.ts);
// Project (divided section, the 5 latest) and Riwayat
// (rows), which scroll; the profile button at the foot with its Menu
// (Profil, Bantuan, Keluar); above it, for a Google account with a
// Non-Penyedia role only, the Mode row that switches the role
// (shell/RoleMode.tsx, a 44px button in the rail); above that the Mode gelap
// switch row (the 44px moon button in the rail). Rows are filtered by GET
// /access/features. A guest gets Chat baru, the menu every user-app role has
// (Generate Dokumen, Regulasi, Dokumen, E-Learning; shell/nav.ts guestNav), each row
// marked with a lock and opening the sign-in gate (shell/SignInGate.tsx)
// instead of the page, and the sign-in prompt (design review ui.region_gate
// guest_signin_card: plain spacing) at the foot, with the same switch row
// directly above the prompt.
// The sidebar sits on layer-1 and is set apart by that tonal step alone,
// never a side line.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { User } from "../api";
import { useAuth } from "../auth";
import { HelpDialog } from "../chat/Help";
import { HistorySection } from "../chat/History";
import { GuestHistory } from "../chat/GuestHistory";
import { canGenerate, ROLE_LABEL } from "../lib/labels";
import { PRODUCT_NAME, PRODUCT_SHORT } from "../lib/product";
import { navigate } from "../router";
import { ShellTitleContext, type PageLayout } from "../ui/components";
import { Menu } from "../ui/Menu";
import { Glyph, type GlyphName } from "../ui/icons";
import { useMedia } from "../chat/support";
import { ChatShellContext, type ChatShellApi } from "./chat-context";
import { AccountBanner, bannerFor, useDriveConnection } from "./AccountBanners";
import { useFeatures } from "./features";
import { GoogleButton } from "./GoogleSignIn";
import { guestNav, LOGIN_PATH, navFor, PROFILE_ITEM, type NavItem } from "./nav";
import { ProjectsProvider, ProjectsSection } from "./Projects";
import { canSwitchRole, RoleModeMenu } from "./RoleMode";
import { SearchProjectsDialog } from "./SearchProjects";
import { SignInGateDialog } from "./SignInGate";
import { ThemeSwitch, ThemeToggle } from "./ThemeToggle";
import { Mark } from "./Shell";

const COLLAPSE_KEY = "procureai.nav.collapsed";

function readCollapsed(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    if (value) localStorage.setItem(COLLAPSE_KEY, "1");
    else localStorage.removeItem(COLLAPSE_KEY);
  } catch {
    // Private mode: the choice lasts for this page only.
  }
}

/** The name the phone header shows per route nav id. */
const SCREEN_NAME: Record<string, string> = {
  asisten: "Chat",
  pengetahuan: "Dokumen",
  belajar: "E-Learning",
  profil: "Profil",
  dokumen: "Dokumen",
  project: "Project",
  dasbor: "Dashboard",
  pantau: "Pantau Paket",
  alur: "Alur Kerja",
  regulasi: "Regulasi",
  template: "Generate Dokumen",
  "dashboard-pbj": "Dashboard PBJ",
  "alur-pbj": "Alur PBJ",
  "project-penyedia": "Project",
  "data-perusahaan": "Data Perusahaan",
  "review-dokumen": "Review Dokumen",
};

/** Destinations the phone bar holds between Chat and Profil (3 to 5 items in all); the rest stay in the drawer. */
const BAR_DESTINATIONS = 3;

/** The open conversation id from the path, or null. */
function conversationFrom(path: string): string | null {
  const m = /^\/asisten\/([^/]+)$/.exec(path);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]!);
  } catch {
    return null;
  }
}

/** The open project id from the path (/project/:id), or null. */
function projectFrom(path: string): string | null {
  const m = /^\/project\/([^/]+)$/.exec(path);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]!);
  } catch {
    return null;
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0]![0]}${parts[parts.length - 1]![0]}` : (parts[0] ?? "?").slice(0, 2);
  return letters.toUpperCase();
}

interface RowLinkProps {
  href: string;
  label: string;
  icon: GlyphName;
  current: boolean;
}

function RowLink({ href, label, icon, current }: RowLinkProps) {
  return (
    <li>
      <a className="cs-item" href={href} aria-current={current ? "page" : undefined} aria-label={label} title={label}>
        <span className="cs-item-icon">
          <Glyph name={icon} size={18} />
        </span>
        <span className="cs-item-label" aria-hidden="true">
          {label}
        </span>
      </a>
    </li>
  );
}

/** A guest's menu row: the page's link, but a plain click opens the sign-in gate. */
function LockedRowLink({ item, onOpen }: { item: NavItem; onOpen: (item: NavItem) => void }) {
  const name = `${item.label}, perlu masuk`;
  return (
    <li>
      <a
        className="cs-item"
        href={item.href}
        data-locked=""
        aria-label={name}
        aria-haspopup="dialog"
        title={name}
        onClick={(e) => {
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          onOpen(item);
        }}
      >
        <span className="cs-item-icon">
          <Glyph name={item.icon} size={18} />
        </span>
        <span className="cs-item-label" aria-hidden="true">
          {item.label}
        </span>
        <span className="cs-item-lock" aria-hidden="true">
          <Glyph name="lock" size={16} />
        </span>
      </a>
    </li>
  );
}

export interface ChatShellProps {
  /** The signed-in user, or null for a guest. */
  user: User | null;
  /** The nav id of the current route ("" for not found). */
  current: string;
  path: string;
  /** The screen fills main and scrolls its own regions (the Chat screen). */
  fill?: boolean;
  /** The page width type (Fase 4): "form" is 760px wide; none is a list page (1200px). */
  layout?: PageLayout;
  children: ReactNode;
}

type Mode = "phone" | "rail" | "full";

export function ChatShell({ user, current, path, fill, layout, children }: ChatShellProps) {
  const tablet = useMedia("(min-width: 768px)");
  const wide = useMedia("(min-width: 1024px)");
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawer, setDrawer] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [gate, setGate] = useState<NavItem | null>(null);
  const [historyTick, setHistoryTick] = useState(0);
  const [renamed, setRenamedState] = useState<{ id: string; title: string } | null>(null);
  const { signOut } = useAuth();
  const features = useFeatures();
  const side = useRef<HTMLElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const main = useRef<HTMLElement>(null);

  const mode: Mode = !tablet ? "phone" : !wide || collapsed ? "rail" : "full";
  const guest = user === null;
  const role = user?.role ?? "pending";
  const chatOn = !guest && features.has("chat");
  const destinations = guest ? [] : navFor(role).filter((d) => features.has(d.feature));
  const locked = guest ? guestNav() : [];
  const conversationId = conversationFrom(path);
  const projectId = projectFrom(path);
  const projectsOn = !guest && features.has("projects");
  const drive = useDriveConnection(user);
  const banner = bannerFor(user, drive);

  const bumpHistory = useCallback(() => setHistoryTick((t) => t + 1), []);
  const setRenamed = useCallback((r: { id: string; title: string }) => setRenamedState(r), []);
  const openHelp = useCallback(() => setHelpOpen(true), []);
  const api = useMemo<ChatShellApi>(() => ({ historyTick, bumpHistory, renamed, setRenamed, openHelp }), [historyTick, bumpHistory, renamed, setRenamed, openHelp]);

  // A route change returns main to the top and closes the drawer.
  useEffect(() => {
    const el = main.current;
    if (el) el.scrollTop = 0;
    setDrawer(false);
  }, [path]);

  // The drawer only exists below 1024.
  useEffect(() => {
    if (wide) setDrawer(false);
  }, [wide]);

  // Opening the drawer moves focus into it; closing returns it to the opener.
  useEffect(() => {
    if (drawer) {
      side.current?.querySelector<HTMLElement>(".cs-top button")?.focus();
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape" && !document.querySelector("dialog[open]")) setDrawer(false);
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }
    opener.current?.focus?.();
    opener.current = null;
  }, [drawer]);

  const openDrawer = (e: { currentTarget: HTMLElement }) => {
    opener.current = e.currentTarget;
    setDrawer(true);
  };

  const toggleCollapse = () => {
    setCollapsed((c) => {
      writeCollapsed(!c);
      return !c;
    });
  };

  const onDeleted = (id: string) => {
    if (conversationId === id) navigate("/");
  };

  const homeName = `${PRODUCT_NAME}, Chat`;
  const railOnly = mode === "rail" && !drawer;
  let topButton: ReactNode;
  if (drawer) {
    topButton = (
      <button type="button" className="ui-icon-btn cs-toggle" aria-label="Tutup menu" title="Tutup menu" aria-controls="cs-side" aria-expanded="true" onClick={() => setDrawer(false)}>
        <Glyph name="closeCircle" />
      </button>
    );
  } else if (mode === "full") {
    topButton = (
      <button type="button" className="ui-icon-btn cs-toggle" aria-label="Ciutkan menu" title="Ciutkan menu" aria-controls="cs-side" aria-expanded="true" onClick={toggleCollapse}>
        <Glyph name="sidebarLeft" />
      </button>
    );
  } else if (wide) {
    topButton = (
      <button type="button" className="ui-icon-btn cs-toggle" aria-label="Luaskan menu" title="Luaskan menu" aria-controls="cs-side" aria-expanded="false" onClick={toggleCollapse}>
        <Glyph name="sidebarLeft" />
      </button>
    );
  } else {
    topButton = (
      <button type="button" className="ui-icon-btn cs-toggle" aria-label="Buka menu" title="Buka menu" aria-controls="cs-side" aria-expanded="false" onClick={openDrawer}>
        <Glyph name="sidebarLeft" />
      </button>
    );
  }

  const profileItems = user
    ? [
        { id: "profil", label: "Profil", onSelect: () => navigate(PROFILE_ITEM.href) },
        { id: "bantuan", label: "Bantuan", onSelect: () => setHelpOpen(true) },
        { id: "keluar", label: "Keluar", onSelect: () => void signOut() },
      ]
    : [];

  // The phone bar: 3 to 5 destinations (JAL app-shell law). It shows a
  // destination's short name ("Dashboard"; a long one wraps and clips at
  // 390px); the full name stays the accessible name.
  const barItems: { id: string; label: string; short?: string; icon: GlyphName; href?: string; onClick?: () => void; current: boolean }[] = guest
    ? [
        { id: "chat", label: "Chat", icon: "messages", href: "/", current: current === "asisten" },
        { id: "bantuan", label: "Bantuan", icon: "messageQuestion", onClick: () => setHelpOpen(true), current: false },
        { id: "masuk", label: "Masuk", icon: "profileCircle", href: LOGIN_PATH, current: false },
      ]
    : [
        { id: "chat", label: "Chat", icon: "messages", href: "/", current: current === "asisten" },
        ...destinations.filter((d) => d.bar !== false).slice(0, BAR_DESTINATIONS).map((d) => ({ id: d.id, label: d.label, short: d.short, icon: d.icon, href: d.href, current: current === d.id })),
        { id: "profil", label: "Profil", icon: "profileCircle", href: PROFILE_ITEM.href, current: current === "profil" },
      ];

  const screenName = SCREEN_NAME[current] ?? PRODUCT_NAME;

  // Cari Paket: one icon button, in the phone header or the sidebar top next to the collapse toggle.
  const searchButton = (
    <button type="button" className="ui-icon-btn cs-find" aria-label="Cari Paket" title="Cari Paket" aria-haspopup="dialog" aria-expanded={searchOpen} onClick={() => setSearchOpen(true)}>
      <Glyph name="searchNormal" />
    </button>
  );

  return (
    <ChatShellContext.Provider value={api}>
      <ProjectsProvider enabled={projectsOn} currentId={projectId}>
      <div className="cs" data-mode={mode} data-drawer={drawer ? "" : undefined} data-guest={guest ? "" : undefined} data-fill={fill ? "" : undefined} data-banner={banner ? "" : undefined}>
        <a className="app-skip" href="#main">
          Langsung ke konten
        </a>

        <header className="cs-header no-print">
          <button type="button" className="ui-icon-btn" aria-label="Buka menu" title="Buka menu" aria-controls="cs-side" aria-expanded={drawer} onClick={openDrawer}>
            <Glyph name="sidebarLeft" />
          </button>
          <a className="cs-header-brand" href="/" aria-label={homeName}>
            <Mark />
          </a>
          <p className="cs-header-title" title={screenName}>
            {screenName}
          </p>
          {projectsOn ? searchButton : null}
          <a className="ui-icon-btn cs-header-new" href="/" aria-label="Chat baru" title="Chat baru">
            <Glyph name="messageAdd" />
          </a>
        </header>

        {drawer ? <div className="cs-scrim" aria-hidden="true" onClick={() => setDrawer(false)} /> : null}

        <aside ref={side} className="cs-side no-print" id="cs-side" aria-label="Menu samping">
          <div className="cs-top">
            <a className="cs-brand" href="/" aria-label={homeName} title={PRODUCT_NAME}>
              <Mark />
              <span className="cs-brand-name" aria-hidden="true">
                {PRODUCT_SHORT}
              </span>
            </a>
            {/* Cari Paket next to the collapse toggle (the drawer has it in the phone header). */}
            {projectsOn && !drawer ? searchButton : null}
            {topButton}
          </div>

          <nav className="side-nav cs-nav" aria-label="Menu utama">
            <ul className="cs-list">
              <RowLink href="/" label="Chat baru" icon="messageAdd" current={path === "/"} />
              {destinations.map((d) => (
                <RowLink key={d.id} href={d.href} label={d.label} icon={d.icon} current={d.id === current} />
              ))}
              {locked.map((d) => (
                <LockedRowLink key={d.id} item={d} onOpen={setGate} />
              ))}
            </ul>
          </nav>

          {railOnly ? (
            <div className="cs-scroll" />
          ) : guest ? (
            // A guest: plain chat only (no project section); the history lives on this device for 24 hours.
            <div className="cs-scroll">
              <GuestHistory />
            </div>
          ) : (
            <div className="cs-scroll">
              {projectsOn ? <ProjectsSection currentId={projectId} /> : null}
              {chatOn ? (
                <HistorySection
                  currentId={conversationId}
                  tick={historyTick}
                  onRenamed={(c) => setRenamed({ id: c.id, title: c.title })}
                  onDeleted={onDeleted}
                />
              ) : null}
            </div>
          )}

          {!user ? (
            <div className="cs-foot cs-foot-guest">
              <div className="cs-theme">{railOnly ? <ThemeToggle /> : <ThemeSwitch />}</div>
              {railOnly ? null : (
                <section className="cs-guest" aria-labelledby="cs-guest-title">
                  <h2 id="cs-guest-title" className="cs-guest-title">
                    Masuk untuk menyimpan riwayat dan membuat project
                  </h2>
                  <p className="cs-guest-text">
                    Riwayat tamu tersimpan di perangkat ini dan terhapus otomatis setelah 24 jam. Masuk dengan Google untuk menyimpan permanen dan membuat project.
                    Percakapan tamu tidak dipindahkan ke akun. Tamu dapat mengirim 5 pesan, lalu jeda 30 menit.
                  </p>
                  <GoogleButton block />
                </section>
              )}
            </div>
          ) : null}
          {user ? (
            <div className="cs-foot">
              <div className="cs-theme">{railOnly ? <ThemeToggle /> : <ThemeSwitch />}</div>
              {canSwitchRole(user) ? <RoleModeMenu user={user} variant={railOnly ? "icon" : "row"} /> : null}
              <Menu
                label="Menu akun"
                items={profileItems}
                align="start"
                placement="above"
                className="cs-profile-menu"
                trigger={({ ref, props }) => (
                  <button
                    ref={ref}
                    type="button"
                    className="cs-item cs-profile"
                    aria-label={`Akun, ${user.name}, ${ROLE_LABEL[user.role]}`}
                    title={`${user.name} (${ROLE_LABEL[user.role]})`}
                    {...props}
                  >
                    <span className="cs-avatar" aria-hidden="true">
                      {initials(user.name)}
                    </span>
                    <span className="cs-person" aria-hidden="true">
                      <span className="cs-person-name">{user.name}</span>
                      <span className="cs-person-role">{ROLE_LABEL[user.role]}</span>
                    </span>
                  </button>
                )}
              />
            </div>
          ) : null}
        </aside>

        <main id="main" ref={main} className="cs-main" tabIndex={-1} data-ui-scroller={fill ? undefined : ""}>
          <AccountBanner kind={banner} />
          <ShellTitleContext.Provider value={screenName}>{fill ? children : <div className="page" data-layout={layout}>{children}</div>}</ShellTitleContext.Provider>
        </main>

        <nav className="app-bar cs-bar no-print" aria-label="Menu bawah">
          {barItems.map((item) =>
            item.href ? (
              <a key={item.id} className="bar-item" href={item.href} aria-current={item.current ? "page" : undefined} aria-label={item.label}>
                <span className="bar-pill">
                  <Glyph name={item.icon} size={24} />
                </span>
                <span className="bar-label" aria-hidden="true">
                  {item.short ?? item.label}
                </span>
              </a>
            ) : (
              <button key={item.id} type="button" className="bar-item" aria-label={item.label} aria-haspopup="dialog" onClick={item.onClick}>
                <span className="bar-pill">
                  <Glyph name={item.icon} size={24} />
                </span>
                <span className="bar-label" aria-hidden="true">
                  {item.short ?? item.label}
                </span>
              </button>
            ),
          )}
        </nav>

        {projectsOn ? <SearchProjectsDialog open={searchOpen} onClose={() => setSearchOpen(false)} /> : null}
        {guest ? <SignInGateDialog item={gate} onClose={() => setGate(null)} /> : null}
        <HelpDialog open={helpOpen} canDraft={user ? canGenerate(user.role) : false} guest={guest} penyedia={user?.role === "penyedia"} onClose={() => setHelpOpen(false)} />
      </div>
      </ProjectsProvider>
    </ChatShellContext.Provider>
  );
}
